/**
 * Velora Modern side nav (model.ts has the layout rules): Home, the user's
 * pins, collapsible categories, the admins' "Not set up" area and the
 * "All modules" browser. Every item has a menu (right-click, Shift+F10 or
 * the "..." button) to pin, unpin, move or hide it; pins also reorder by
 * drag and drop or Alt+Shift+Up / Alt+Shift+Down on a focused pin (Alt+Up
 * alone is left to the pages). The collapsed rail keeps icons with
 * tooltips, and each category becomes a flyout. The collapse toggle sits
 * at the top beside the logo; the footer stays pinned at the bottom.
 *
 * Familiar modes keep their own chrome (ClassicFrame) and never render this.
 */
import { useNavigate } from '@tanstack/react-router';
import { ContextMenu, Menu, Tooltip, type MenuEntry, type NavLinkRenderProps } from '@velora/ui';
import {
  AppWindow,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  EyeOff,
  Keyboard,
  LayoutGrid,
  Lock,
  MoreHorizontal,
  Pin,
  PinOff,
  Plug,
  Puzzle,
  Wrench,
} from 'lucide-react';
import { useMemo, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Icon } from '../../icons.js';
import { splitHref } from '../../patient/follow.js';
import { inPopout, useOpenInWindow } from '../../windows/PopoutHereButton.js';
import { CollapseToggle } from '../subnav/collapse.js';
import { AllModulesDialog } from './AllModulesDialog.js';
import {
  arrangeNav,
  dropPin,
  hide,
  MERGED_INTO,
  movePin,
  navDefaults,
  pin,
  toggleCategory,
  unpin,
  type ArrangeOptions,
  type NavEntryIn,
  type NavRole,
} from './model.js';
import { useIntegrations, useNavLayout } from './useNavLayout.js';
import './sidenav.css';

export type SideNavEntry = NavEntryIn & { iconNode: ReactNode };

export interface VeloraSideNavProps {
  entries: readonly SideNavEntry[];
  activeId?: string | undefined;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  renderLink: (props: NavLinkRenderProps) => ReactNode;
  header: ReactNode;
  onShortcuts: () => void;
  /** May set integrations up (settings.manage): sees "Not set up". */
  canSetUp: boolean;
  /** Test seam: integration states instead of the live query. */
  integrations?: ArrangeOptions['integrations'] | undefined;
  /** The person's role: their starting pins, hidden items and collapsed groups. */
  role?: NavRole | undefined;
}

/**
 * Add-on annotation: a small puzzle mark (the "Add-on" tag in All modules
 * and the Marketplace), or a lock when the offices in scope do not have it.
 */
function AddOnMark({ locked }: { locked?: boolean | undefined }) {
  const text = locked ? 'Add-on, not on for this office: opens its details' : 'Add-on module';
  return (
    <span className={`vsn-addon ${locked ? 'is-locked' : ''}`} title={text}>
      {locked ? <Lock size={11} aria-hidden /> : <Puzzle size={12} aria-hidden />}
      <span className="vsn-sr">{locked ? ' (add-on, not on)' : ' (add-on)'}</span>
    </span>
  );
}

export function VeloraSideNav(props: VeloraSideNavProps) {
  const {
    entries,
    activeId: activeIdIn,
    collapsed,
    onCollapsedChange,
    renderLink,
    header,
    canSetUp,
  } = props;
  const [layout, save] = useNavLayout();
  const live = useIntegrations();
  const integrations = props.integrations ?? live;
  const [browserOpen, setBrowserOpen] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const navigate = useNavigate();

  const defaults = useMemo(() => navDefaults(props.role), [props.role]);
  const nav = useMemo(
    () => arrangeNav(entries, layout, { integrations, canSetUp, defaults }),
    [entries, layout, integrations, canSetUp, defaults],
  );
  const pinIds = nav.pinned.map((p) => p.id);
  // On a merged page (Close the day), light up the item it is a tab of.
  const drawn = new Set([...pinIds, ...nav.categories.flatMap((c) => c.items.map((i) => i.id))]);
  const parentId = activeIdIn ? MERGED_INTO[activeIdIn] : undefined;
  const activeId =
    activeIdIn && !drawn.has(activeIdIn) && parentId && drawn.has(parentId) ? parentId : activeIdIn;

  // QA r57: pop-outs were only reachable from the top-bar menu.
  const openInWindow = useOpenInWindow();
  const menuFor = (e: SideNavEntry, where: 'pinned' | 'group'): MenuEntry[] => {
    const i = pinIds.indexOf(e.id);
    const win: MenuEntry[] = inPopout()
      ? []
      : [
          {
            id: 'new-window',
            label: 'Open in new window',
            icon: <AppWindow size={14} />,
            onSelect: () => openInWindow(e.href, e.id, e.label),
          },
          { type: 'separator', id: 's-win' },
        ];
    const rest: MenuEntry[] =
      where === 'pinned'
        ? [
            {
              id: 'unpin',
              label: 'Unpin',
              icon: <PinOff size={14} />,
              onSelect: () => save(unpin(layout, e.id, defaults)),
            },
            {
              id: 'up',
              label: 'Move up',
              icon: <ArrowUp size={14} />,
              shortcut: ['Alt', 'Shift', 'Up'],
              disabled: i <= 0,
              onSelect: () => save(movePin(layout, e.id, -1, pinIds, defaults)),
            },
            {
              id: 'down',
              label: 'Move down',
              icon: <ArrowDown size={14} />,
              shortcut: ['Alt', 'Shift', 'Down'],
              disabled: i === pinIds.length - 1,
              onSelect: () => save(movePin(layout, e.id, 1, pinIds, defaults)),
            },
            { type: 'separator', id: 's' },
            {
              id: 'hide',
              label: 'Hide from sidebar',
              icon: <EyeOff size={14} />,
              onSelect: () => save(hide(layout, e.id, defaults)),
            },
          ]
        : [
            {
              id: 'pin',
              label: 'Pin to top',
              icon: <Pin size={14} />,
              onSelect: () => save(pin(layout, e.id, defaults)),
            },
            {
              id: 'hide',
              label: 'Hide from sidebar',
              icon: <EyeOff size={14} />,
              onSelect: () => save(hide(layout, e.id, defaults)),
            },
          ];
    return [...win, ...rest];
  };

  const link = (e: SideNavEntry, onKeyDown?: (ev: KeyboardEvent) => void) => {
    const active = e.id === activeId;
    const node = renderLink({
      href: e.href,
      className: 'vel-nav__item',
      'aria-current': active ? 'page' : undefined,
      children: (
        <>
          <span className="vel-nav__icon" aria-hidden>
            {e.iconNode}
          </span>
          <span className="vel-nav__label">{e.label}</span>
          {e.badge ? (
            <span
              className={`vsn-badge ${e.badgeTone === 'danger' ? 'is-danger' : ''}`}
              aria-label={`${e.badge} waiting`}
            >
              {e.badge > 99 ? '99+' : e.badge}
            </span>
          ) : null}
          {!collapsed && (e.addOn || e.locked) ? <AddOnMark locked={e.locked} /> : null}
        </>
      ),
    });
    return onKeyDown ? (
      <span className="vsn-keys" onKeyDown={onKeyDown}>
        {node}
      </span>
    ) : (
      node
    );
  };

  /** The item's hover hint (useNavHint), e.g. which AI Receptionist modes are on. */
  const hinted = (e: SideNavEntry, node: ReactNode) =>
    e.hint ? (
      <Tooltip side="right" content={e.hint}>
        <span className="vel-nav__tip-anchor" data-testid={`nav-hint-${e.moduleId}`}>
          {node}
        </span>
      </Tooltip>
    ) : (
      node
    );

  const row = (e: SideNavEntry, where: 'pinned' | 'group') => {
    const items = menuFor(e, where);
    const onKeyDown =
      where === 'pinned'
        ? (ev: KeyboardEvent) => {
            if (!ev.altKey || !ev.shiftKey || (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown'))
              return;
            ev.preventDefault();
            ev.stopPropagation();
            save(movePin(layout, e.id, ev.key === 'ArrowUp' ? -1 : 1, pinIds, defaults));
          }
        : undefined;
    const drag =
      where === 'pinned' && !collapsed
        ? {
            draggable: true,
            onDragStart: (ev: DragEvent) => {
              setDragId(e.id);
              ev.dataTransfer.effectAllowed = 'move';
              ev.dataTransfer.setData('text/plain', e.id);
            },
            onDragOver: (ev: DragEvent) => {
              if (dragId && dragId !== e.id) ev.preventDefault();
            },
            onDrop: (ev: DragEvent) => {
              ev.preventDefault();
              const from = dragId ?? ev.dataTransfer.getData('text/plain');
              if (from) save(dropPin(layout, from, e.id, defaults));
              setDragId(null);
            },
            onDragEnd: () => setDragId(null),
          }
        : {};
    const tip = [e.badge ? `${e.label} (${e.badge})` : e.label, e.hint].filter(Boolean).join(' · ');
    const body = collapsed ? (
      <Tooltip side="right" content={tip}>
        <span className="vel-nav__tip-anchor">{link(e, onKeyDown)}</span>
      </Tooltip>
    ) : (
      <div className="vsn-row">
        {hinted(e, link(e, onKeyDown))}
        <Menu
          side="right"
          align="start"
          items={items}
          trigger={
            <button
              type="button"
              className="vsn-more"
              tabIndex={-1}
              aria-label={`${e.label} options`}
            >
              <MoreHorizontal size={14} aria-hidden />
            </button>
          }
        />
      </div>
    );
    return (
      <li
        key={e.id}
        data-nav-id={e.id}
        className={dragId === e.id ? 'is-dragging' : undefined}
        {...drag}
      >
        <ContextMenu items={items}>
          <div className="vsn-ctx">{body}</div>
        </ContextMenu>
      </li>
    );
  };

  const flyout = (label: string, icon: ReactNode, entriesIn: readonly SideNavEntry[]) => (
    <li key={label}>
      <Menu
        side="right"
        align="start"
        items={[
          { type: 'label', id: 'l', label },
          ...entriesIn.map((e) => ({
            id: e.id,
            label: e.badge ? `${e.label} (${e.badge})` : e.label,
            icon: e.iconNode,
            onSelect: () => void navigate(splitHref(e.href) as never),
          })),
        ]}
        trigger={
          <button
            type="button"
            className="vel-nav__item vsn-flyout"
            aria-label={label}
            title={label}
            aria-current={entriesIn.some((e) => e.id === activeId) ? 'true' : undefined}
          >
            <span className="vel-nav__icon" aria-hidden>
              {icon}
            </span>
          </button>
        }
      />
    </li>
  );

  return (
    <nav className="vel-nav vsn" data-collapsed={collapsed ? '' : undefined} aria-label="Main">
      {/* The collapse toggle sits at the top, beside the logo (Zac
          2026-10-29: in the footer it slid off the bottom of the window). */}
      <div className="vel-nav__header">
        {header}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => onCollapsedChange(!collapsed)}
          label="navigation"
          className="vel-nav__collapse"
          side={collapsed ? 'right' : 'bottom'}
        />
      </div>
      <div className="vel-nav__scroll vsn-scroll">
        {nav.home || nav.top.length > 0 ? (
          <ul className="vel-nav__list">
            {nav.home ? row(nav.home as SideNavEntry, 'group') : null}
            {nav.top.map((e) => row(e as SideNavEntry, 'group'))}
          </ul>
        ) : null}

        <section className="vsn-section" aria-label="Pinned">
          {collapsed ? (
            <div className="vel-nav__group-sep" role="separator" aria-label="Pinned" />
          ) : (
            <div className="vsn-head vsn-head--static">
              <Pin size={11} aria-hidden />
              <span>Pinned</span>
            </div>
          )}
          {nav.pinned.length > 0 ? (
            <ul className="vel-nav__list">
              {nav.pinned.map((e) => row(e as SideNavEntry, 'pinned'))}
            </ul>
          ) : collapsed ? null : (
            <p className="vsn-empty">Right-click any item, then Pin to top.</p>
          )}
        </section>

        {collapsed ? (
          <ul className="vel-nav__list vsn-rail-groups">
            {nav.categories.map((c) =>
              flyout(c.label, <Icon name={c.icon} size={17} />, c.items as SideNavEntry[]),
            )}
          </ul>
        ) : (
          nav.categories.map((c) => {
            const listId = `vsn-cat-${c.id}`;
            return (
              <section key={c.id} className="vsn-section" aria-label={c.label}>
                <button
                  type="button"
                  className="vsn-head"
                  aria-expanded={!c.collapsed}
                  aria-controls={listId}
                  onClick={() => save(toggleCategory(layout, c.id, defaults))}
                >
                  <span className="vsn-head__icon" aria-hidden>
                    <Icon name={c.icon} size={13} />
                  </span>
                  <span className="vsn-head__label">{c.label}</span>
                  {c.collapsed ? <span className="vsn-count">{c.items.length}</span> : null}
                  <ChevronDown size={13} aria-hidden className="vsn-chev" />
                </button>
                {c.collapsed ? null : (
                  <ul id={listId} className="vel-nav__list">
                    {c.items.map((e) => row(e as SideNavEntry, 'group'))}
                  </ul>
                )}
              </section>
            );
          })
        )}

        {nav.notSetUp.length > 0 ? (
          collapsed ? (
            <ul className="vel-nav__list vsn-rail-groups">
              {flyout(
                'Not set up',
                <Plug size={16} />,
                nav.notSetUp.map((n) => n.entry as SideNavEntry),
              )}
            </ul>
          ) : (
            <section className="vsn-section vsn-unset" aria-label="Not set up">
              <button
                type="button"
                className="vsn-head"
                aria-expanded={nav.notSetUpOpen}
                aria-controls="vsn-unset-list"
                onClick={() => save({ ...layout, notSetUpOpen: !nav.notSetUpOpen })}
              >
                <span>Not set up</span>
                <span className="vsn-count">{nav.notSetUp.length}</span>
                <ChevronDown size={13} aria-hidden className="vsn-chev" />
              </button>
              {nav.notSetUpOpen ? (
                <ul id="vsn-unset-list" className="vel-nav__list">
                  {nav.notSetUp.map((n) => (
                    <li key={n.entry.id} className="vsn-unset__row" data-nav-id={n.entry.id}>
                      {hinted(n.entry as SideNavEntry, link(n.entry as SideNavEntry))}
                      {renderLink({
                        href: n.setupHref,
                        className: 'vsn-setup',
                        'aria-current': undefined,
                        children: (
                          <>
                            <Wrench size={11} aria-hidden />
                            <span>Set up</span>{' '}
                            <span className="vsn-sr">{` ${n.entry.label}`}</span>
                          </>
                        ),
                      })}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          )
        ) : null}
      </div>

      <div className="vel-nav__footer vsn-footer">
        {nav.footer.length > 0 ? (
          <ul className="vel-nav__list vsn-fixed" aria-label="Platform">
            {nav.footer.map((e) => (
              <li key={e.id} data-nav-id={e.id}>
                {collapsed ? (
                  <Tooltip side="right" content={e.label}>
                    <span className="vel-nav__tip-anchor">{link(e as SideNavEntry)}</span>
                  </Tooltip>
                ) : (
                  link(e as SideNavEntry)
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {collapsed ? (
          <Tooltip side="right" content="All modules">
            <button
              type="button"
              className="vel-nav__item vsn-all"
              aria-label="All modules"
              onClick={() => setBrowserOpen(true)}
            >
              <span className="vel-nav__icon" aria-hidden>
                <LayoutGrid size={16} />
              </span>
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className="vel-nav__item vsn-all"
            onClick={() => setBrowserOpen(true)}
          >
            <span className="vel-nav__icon" aria-hidden>
              <LayoutGrid size={16} />
            </span>
            <span className="vel-nav__label">All modules</span>
            {nav.hiddenCount > 0 ? (
              <span
                className="vsn-count"
                title={`${nav.hiddenCount} hidden from the sidebar`}
                aria-label={`${nav.hiddenCount} hidden`}
              >
                {nav.hiddenCount}
              </span>
            ) : null}
          </button>
        )}
        <div className="vsn-footer__tools">
          <Tooltip side={collapsed ? 'right' : 'top'} content="Keyboard shortcuts">
            <button
              type="button"
              className="vel-nav__item vsn-icon-btn shell-nav-help"
              onClick={props.onShortcuts}
              aria-label="Keyboard shortcuts"
            >
              <span className="vel-nav__icon" aria-hidden>
                <Keyboard size={16} />
              </span>
            </button>
          </Tooltip>
        </div>
      </div>

      <AllModulesDialog
        open={browserOpen}
        onOpenChange={setBrowserOpen}
        nav={nav}
        layout={layout}
        defaults={defaults}
        onChange={save}
        onOpenItem={(href) => {
          setBrowserOpen(false);
          void navigate(splitHref(href) as never);
        }}
      />
    </nav>
  );
}
